import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useSiteSettings } from "@/lib/useSiteSettings";
import { Instagram, Youtube, Twitter, Facebook, Linkedin, Music2 } from "lucide-react";

const SECTIONS = [
  {
    title: "Nearfluence",
    links: [
      { label: "About Us", to: "/about" },
      { label: "How Nearfluence Works", to: "/how-it-works" },
      { label: "Contact Us", to: "/contact" },
      { label: "FAQ", to: "/faq" },
    ],
  },
  {
    title: "For Creators",
    links: [
      { label: "How It Works for Creators", to: "/how-it-works#creators" },
      { label: "Opportunities", to: "/opportunities" },
      { label: "Creator Guidelines", to: "/creator-guidelines" },
    ],
  },
  {
    title: "For Businesses",
    links: [
      { label: "How It Works for Businesses", to: "/how-it-works#businesses" },
      { label: "Discover Creators", to: "/discover" },
      { label: "Business Guidelines", to: "/business-guidelines" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms of Use", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Cookie Policy", to: "/cookies" },
      { label: "Community Guidelines", to: "/community-guidelines" },
      { label: "Legal & Disclaimer", to: "/legal" },
    ],
  },
];

const SOCIAL_ICONS = { instagram: Instagram, tiktok: Music2, youtube: Youtube, twitter: Twitter, facebook: Facebook, linkedin: Linkedin };

export function Footer() {
  const settings = useSiteSettings();
  const social = settings?.social || {};
  const activeSocials = Object.keys(SOCIAL_ICONS).filter((k) => social[k]);

  return (
    <footer data-testid="site-footer" className="border-t border-zinc-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-5">
          <div className="col-span-2 md:col-span-1">
            <Logo />
            <p className="mt-3 max-w-xs text-sm text-zinc-500">Local creators. Local businesses. Real influence — right across the UK.</p>
            {activeSocials.length > 0 && (
              <div className="mt-4 flex gap-3">
                {activeSocials.map((k) => {
                  const Icon = SOCIAL_ICONS[k];
                  return (
                    <a key={k} href={social[k]} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${k}`}
                      className="text-zinc-400 transition-colors hover:text-emerald-600"><Icon className="h-5 w-5" /></a>
                  );
                })}
              </div>
            )}
          </div>
          {SECTIONS.map((s) => (
            <div key={s.title}>
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-zinc-900">{s.title}</h3>
              <ul className="mt-4 space-y-2.5">
                {s.links.map((l) => (
                  <li key={l.label}>
                    <Link data-testid={`footer-link-${l.to.replace(/[/#]/g, "-").replace(/^-+/, "")}`} to={l.to} className="text-sm text-zinc-500 transition-colors hover:text-emerald-600">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-zinc-100 pt-6 sm:flex-row">
          <p className="text-xs text-zinc-400">© 2026 {settings?.company_name || "Nearfluence"}. All rights reserved.</p>
          <p className="text-xs text-zinc-400">Made for the UK creator community</p>
        </div>
      </div>
    </footer>
  );
}
